import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function FAQ() {
  const [cartCount, setCartCount] = useState(0);
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  useEffect(() => {
    const updateCartCount = () => {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        const cart = JSON.parse(savedCart);
        const count = cart.reduce((sum: number, item: any) => sum + item.quantity, 0);
        setCartCount(count);
      }
    };
    
    updateCartCount();
    window.addEventListener('storage', updateCartCount);
    window.addEventListener('cartUpdated', updateCartCount);
    
    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cartUpdated', updateCartCount);
    };
  }, []);

  const faqs = [
    {
      category: 'Orders & Payments',
      questions: [
        {
          q: 'How do I place an order?',
          a: 'Simply browse our products, add items to your cart, and proceed to checkout. Fill in your shipping details and payment information to complete your order. You will receive an order confirmation email immediately.'
        },
        {
          q: 'What payment methods do you accept?',
          a: 'We accept all major credit cards (Visa, Mastercard, American Express), debit cards, and secure online payment methods. All transactions are SSL encrypted for your security.'
        },
        {
          q: 'Is my payment information secure?',
          a: 'Absolutely! We use industry-standard SSL encryption to protect your payment information. We never store your complete credit card details on our servers.'
        },
        {
          q: 'Can I modify or cancel my order?',
          a: 'You can modify or cancel your order within 2 hours of placing it. Contact our customer support immediately at +1 (929) 268-8770 or email sam4104t@gmail.com.'
        }
      ]
    },
    {
      category: 'Shipping & Delivery',
      questions: [
        {
          q: 'How long does shipping take?',
          a: 'Standard international shipping takes 7-15 business days. Express shipping options are available at checkout for faster delivery (3-7 business days).'
        },
        {
          q: 'Do you offer free shipping?',
          a: 'Yes! We offer free worldwide shipping on all orders. No minimum purchase required.'
        },
        {
          q: 'How can I track my order?',
          a: 'Once your order ships, you will receive a tracking number via email. You can use this number to track your package in real-time on our website or the carrier\'s website.'
        },
        {
          q: 'What if my package is lost or damaged?',
          a: 'If your package is lost or arrives damaged, contact us immediately. We will reship your order at no additional cost or provide a full refund.'
        },
        {
          q: 'Do you ship internationally?',
          a: 'Yes! We ship to over 150 countries worldwide. Shipping times may vary depending on your location and customs clearance.'
        }
      ]
    },
    {
      category: 'Products & Prescriptions',
      questions: [
        {
          q: 'Are all your medicines genuine?',
          a: 'Yes, 100%! All our medications are sourced directly from licensed manufacturers and are FDA approved. We guarantee authenticity and quality for every product.'
        },
        {
          q: 'Do I need a prescription?',
          a: 'For prescription medications, yes. Our licensed pharmacists will review your order and may contact you to verify prescription details. Some products are available over-the-counter without a prescription.'
        },
        {
          q: 'How do I submit my prescription?',
          a: 'You can upload your prescription during checkout, email it to sam4104t@gmail.com, or have your doctor send it directly to us. Our pharmacists will verify it before processing your order.'
        },
        {
          q: 'What if I have questions about a medication?',
          a: 'Our licensed pharmacists are available 24/7 to answer your questions. Call us at +1 (929) 268-8770 or use our live chat for immediate assistance.'
        },
        {
          q: 'Can I order medications for someone else?',
          a: 'Yes, you can order on behalf of someone else. However, the prescription must be in the patient\'s name, and we may need to verify the prescription with the prescribing doctor.'
        }
      ]
    },
    {
      category: 'Returns & Refunds',
      questions: [
        {
          q: 'What is your return policy?',
          a: 'We offer a 30-day money-back guarantee. If you\'re not satisfied with your purchase, contact us within 30 days for a full refund or replacement.'
        },
        {
          q: 'How do I return a product?',
          a: 'Contact our customer support team to initiate a return. We will provide you with return instructions and a prepaid shipping label if applicable.'
        },
        {
          q: 'When will I receive my refund?',
          a: 'Refunds are processed within 5-7 business days after we receive your returned item. The refund will be credited to your original payment method.'
        },
        {
          q: 'Can I return opened medications?',
          a: 'For safety and regulatory reasons, we cannot accept returns of opened or used medications. However, if you received a defective or incorrect product, we will replace it immediately.'
        }
      ]
    },
    {
      category: 'Account & Privacy',
      questions: [
        {
          q: 'Do I need to create an account to order?',
          a: 'No, you can checkout as a guest. However, creating an account allows you to track orders, save shipping addresses, and access exclusive deals.'
        },
        {
          q: 'Is my personal information secure?',
          a: 'Yes! We take your privacy seriously. All personal information is encrypted and stored securely. We never share your information with third parties. Read our Privacy Policy for details.'
        },
        {
          q: 'How is my order packaged?',
          a: 'All orders are packaged discreetly in plain, unmarked packaging with no indication of contents. Your privacy is our priority.'
        },
        {
          q: 'Can I change my shipping address?',
          a: 'Yes, you can update your shipping address in your account settings before your order ships. Contact us immediately if your order has already been dispatched.'
        }
      ]
    },
    {
      category: 'Customer Support',
      questions: [
        {
          q: 'How can I contact customer support?',
          a: 'We\'re available 24/7! Call us at +1 (929) 268-8770, email sam4104t@gmail.com, or use WhatsApp at +1 (929) 268-8770. You can also use our live chat on the website.'
        },
        {
          q: 'What are your business hours?',
          a: 'Our customer support team is available 24 hours a day, 7 days a week, including holidays. We\'re always here to help!'
        },
        {
          q: 'Do you offer phone consultations with pharmacists?',
          a: 'Yes! Our licensed pharmacists are available for free consultations. Call us anytime to discuss your medications, side effects, or health concerns.'
        },
        {
          q: 'How quickly do you respond to emails?',
          a: 'We typically respond to emails within 2-4 hours during business hours and within 24 hours on weekends and holidays.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <div className="bg-blue-600 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center text-sm">
            <div className="flex items-center space-x-6">
              <span className="flex items-center">
                <i className="ri-phone-line mr-2"></i>
                +1 (929) 268-8770
              </span>
              <span className="flex items-center">
                <i className="ri-mail-line mr-2"></i>
                sam4104t@gmail.com
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="flex items-center">
                <i className="ri-customer-service-2-line mr-2"></i>
                24/7 Support Available
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-lg mr-3">
                <i className="ri-medicine-bottle-line text-2xl text-white"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-blue-600">PillsForU</h1>
                <p className="text-xs text-gray-500">Trusted Healthcare</p>
              </div>
            </Link>

            <div className="flex items-center space-x-6">
              <Link to="/cart" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors cursor-pointer relative">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-shopping-cart-line text-2xl"></i>
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">{cartCount}</span>
                  )}
                </div>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
            <i className="ri-question-answer-line text-5xl"></i>
          </div>
          <h1 className="text-5xl font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-xl text-blue-100">Find answers to common questions about our products and services</p>
        </div>
      </div>

      {/* Quick Contact */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-gradient-to-r from-green-500 to-teal-500 rounded-2xl p-8 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">Can't Find Your Answer?</h2>
          <p className="text-lg mb-6 text-green-50">Our support team is available 24/7 to help you with any questions</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+19292688770" className="bg-white text-green-600 px-8 py-3 rounded-lg hover:bg-green-50 transition-colors whitespace-nowrap cursor-pointer font-bold inline-block">
              <i className="ri-phone-line mr-2"></i>
              Call Us Now
            </a>
            <Link to="/contact" className="border-2 border-white text-white px-8 py-3 rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer font-bold inline-block">
              <i className="ri-mail-line mr-2"></i>
              Send Message
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {faqs.map((section, sectionIndex) => (
          <div key={sectionIndex} className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-lg mr-3">
                <i className="ri-folder-line text-xl text-blue-600"></i>
              </div>
              {section.category}
            </h2>
            <div className="space-y-4">
              {section.questions.map((faq, index) => {
                const globalIndex = sectionIndex * 100 + index;
                const isOpen = openIndex === globalIndex;
                
                return (
                  <div key={index} className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden hover:border-blue-300 transition-colors">
                    <button
                      onClick={() => setOpenIndex(isOpen ? null : globalIndex)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-lg font-bold text-gray-900 pr-4">{faq.q}</h3>
                      <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                        <i className="ri-arrow-down-s-line text-2xl text-blue-600"></i>
                      </div>
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-5 pt-2">
                        <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      {/* Still Have Questions */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Still Have Questions?</h2>
            <p className="text-lg text-gray-600">We're here to help you 24/7</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-md text-center hover:shadow-xl transition-shadow border border-gray-200">
              <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-4">
                <i className="ri-phone-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Call Us</h3>
              <p className="text-gray-600 mb-4">Speak with our pharmacists</p>
              <a href="tel:+19292688770" className="text-blue-600 font-semibold hover:text-blue-700 cursor-pointer">+1 (929) 268-8770</a>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-md text-center hover:shadow-xl transition-shadow border border-gray-200">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                <i className="ri-mail-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Email Us</h3>
              <p className="text-gray-600 mb-4">Get a response within 24 hours</p>
              <a href="mailto:sam4104t@gmail.com" className="text-blue-600 font-semibold hover:text-blue-700 cursor-pointer">sam4104t@gmail.com</a>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-md text-center hover:shadow-xl transition-shadow border border-gray-200">
              <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-full mx-auto mb-4">
                <i className="ri-whatsapp-line text-3xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">WhatsApp</h3>
              <p className="text-gray-600 mb-4">Chat with us instantly</p>
              <a href="https://wa.me/19292688770" className="text-blue-600 font-semibold hover:text-blue-700 cursor-pointer">Start Chat</a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="text-white font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="hover:text-blue-400 transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="hover:text-blue-400 transition-colors cursor-pointer">About Us</Link></li>
                <li><Link to="/all-products" className="hover:text-blue-400 transition-colors cursor-pointer">All Products</Link></li>
                <li><Link to="/contact" className="hover:text-blue-400 transition-colors cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/privacy-policy" className="hover:text-blue-400 transition-colors cursor-pointer">Privacy Policy</Link></li>
                <li><Link to="/terms-conditions" className="hover:text-blue-400 transition-colors cursor-pointer">Terms &amp; Conditions</Link></li>
                <li><Link to="/refund-policy" className="hover:text-blue-400 transition-colors cursor-pointer">Refund Policy</Link></li>
                <li><Link to="/shipping-policy" className="hover:text-blue-400 transition-colors cursor-pointer">Shipping Policy</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <p>Phone: +1 (929) 268-8770</p>
              <p>Email: sam4104t@gmail.com</p>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Follow Us</h4>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-twitter-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-fill"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400 text-sm">&copy; 2024 PillsForU.com. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-blue-400 transition-colors cursor-pointer">Powered by Readdy</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
